import {
  Users,
  ClipboardCheck,
  TrendingUp,
  Award,
  StickyNote,
  Plane,
  Calendar,
  PartyPopper,
  Clock,
  BarChart3,
  Sparkles,
} from "lucide-react";

import {
  WeatherWidget,
  TotalEmployeesWidget,
  AttendanceSummaryWidget,
  PerformanceWidget,
  StarOfTheMonthWidget,
  DateTimeAttendanceWidget,
  DailyBriefingWidget,
  PersonalNotesWidget,
  EmployeesOnLeaveWidget,
  UpcomingHolidaysWidget,
  UpcomingCelebrationsWidget,
  LeaveBalanceWidget,
} from "./index";
import ReportsPanel from "../../reports/ReportsPanel";
import { hasPermission, hasAnyPermission } from "../../../utils/permissionUtils";

/**
 * Central registry of all dashboard widgets.
 *
 *   id: stable key, used in layouts + DB
 *   title: human label for the Personalise page toggles
 *   description: one-line summary shown on the widget catalogue cards
 *   icon: lucide-react icon component (optional — weather renders its own)
 *   Component: self-contained React component
 *   className: optional extra className forwarded to the .dash-widget wrapper
 *   minW/minH: grid constraints for react-grid-layout
 *   resizable: set true to show the resize handle in Personalise mode —
 *              opt-in, so every other widget keeps its size (still draggable)
 *   fixedSize: { w, h } pins the widget to this footprint (overrides any
 *              stale saved size on render — still draggable)
 *   allowedRoles: which dashboards can see this widget
 *   permission: optional Roles & Access key, or an array meaning "any of".
 *               When set, the widget is also offered on ANY dashboard to a
 *               holder of that key — a custom role (e.g. "Manager") always
 *               lands on the member dashboard, so allowedRoles alone could
 *               never reach it. Set it to whatever the widget's API needs, so
 *               nobody is shown a widget that can only 403 into zeros.
 */
export const WIDGET_REGISTRY = {
  weather: {
    id: "weather",
    title: "Weather",
    description: "Live local weather and conditions.",
    icon: null, // Weather widget renders its own condition-driven icon
    Component: WeatherWidget,
    className: "weather-wrap",
    minW: 2,
    minH: 2,
    resizable: true,
    allowedRoles: ["admin", "hr", "member"],
  },
  "total-employees": {
    id: "total-employees",
    title: "Total Employees",
    description: "Active headcount at a glance.",
    icon: Users,
    Component: TotalEmployeesWidget,
    className: "total-emp",
    minW: 3,
    minH: 1,
    // Compact stat strip — pinned size, no resize handle, but still draggable.
    // fixedSize re-applies even over a stale saved layout.
    fixedSize: { w: 3, h: 1 },
    allowedRoles: ["admin", "hr"],
    permission: "manage_employees",
  },
  "attendance-summary": {
    id: "attendance-summary",
    title: "Today's Attendance Summary",
    description: "Present, on-leave and absent counts for today.",
    icon: ClipboardCheck,
    Component: AttendanceSummaryWidget,
    className: "att-summary",
    minW: 3,
    minH: 3,
    // Designed for a fixed footprint — pin it, no resize handle, but still
    // draggable. fixedSize re-applies over stale layouts.
    fixedSize: { w: 3, h: 3 },
    allowedRoles: ["admin", "hr", "member"],
    // Company board (/attendance/company) or the caller's team (/attendance/team).
    permission: ["manage_attendance", "view_team_attendance"],
  },
  performance: {
    id: "performance",
    title: "Performance",
    description: "Today's company attendance, on-time and late rates.",
    icon: TrendingUp,
    Component: PerformanceWidget,
    className: "perform-wrap",
    minW: 3,
    minH: 2,
    allowedRoles: ["admin", "hr", "member"],
    // Same key GET /api/attendance/analytics is gated on.
    permission: "view_attendance_analytics",
  },
  "star-of-the-month": {
    id: "star-of-the-month",
    title: "Star of the Month",
    description: "Celebrate the month's standout colleague.",
    icon: Award,
    Component: StarOfTheMonthWidget,
    className: "emp-leave-slider",
    minW: 2,
    minH: 3,
    allowedRoles: ["admin", "hr", "member"],
  },
  "date-time-attendance": {
    id: "date-time-attendance",
    title: "Today",
    description: "Date, live clock and your mark-in / out.",
    icon: Clock,
    Component: DateTimeAttendanceWidget,
    className: "atnds-time",
    minW: 4,
    minH: 2,
    // Compact glass strip — pinned footprint, no resize handle, still
    // draggable. fixedSize re-applies even over a stale saved layout.
    fixedSize: { w: 6, h: 2 },
    allowedRoles: ["admin", "hr", "member"],
  },
  "daily-briefing": {
    id: "daily-briefing",
    title: "Daily Briefing",
    description: "Your day at a glance: events, tasks due, approvals and who's out.",
    icon: Sparkles,
    Component: DailyBriefingWidget,
    className: "brief-wrap",
    minW: 3,
    minH: 5,
    resizable: true,
    allowedRoles: ["admin", "hr", "member"],
  },
  "personal-notes": {
    id: "personal-notes",
    title: "Personal Notes",
    description: "Your sticky notes and checklists.",
    icon: StickyNote,
    Component: PersonalNotesWidget,
    className: "personal-note",
    minW: 2,
    minH: 2,
    resizable: true,
    allowedRoles: ["admin", "hr", "member"],
  },
  "employees-on-leave": {
    id: "employees-on-leave",
    title: "Employees on Leave Today",
    description: "Who's off today.",
    icon: Plane,
    Component: EmployeesOnLeaveWidget,
    className: "emp-leave-slider",
    minW: 3,
    minH: 4,
    // Gradient-panel template — pinned footprint, no resize handle, still
    // draggable. fixedSize re-applies even over a stale saved layout.
    fixedSize: { w: 3, h: 4 },
    allowedRoles: ["admin", "hr", "member"],
  },
  "upcoming-holidays": {
    id: "upcoming-holidays",
    title: "Upcoming Holidays",
    description: "The next company holidays.",
    icon: Calendar,
    Component: UpcomingHolidaysWidget,
    className: "holiday-wrap",
    minW: 3,
    minH: 3,
    fixedSize: { w: 3, h: 3 },
    allowedRoles: ["admin", "hr", "member"],
  },
  "leave-balance": {
    id: "leave-balance",
    title: "My Leave Balance",
    description: "Remaining leave days by type for you.",
    icon: Plane,
    Component: LeaveBalanceWidget,
    className: "leave-balance-wrap",
    minW: 3,
    minH: 3,
    allowedRoles: ["admin", "hr", "member"],
  },
  "upcoming-celebrations": {
    id: "upcoming-celebrations",
    title: "Upcoming Celebrations",
    description: "Birthdays and work anniversaries.",
    icon: PartyPopper,
    Component: UpcomingCelebrationsWidget,
    className: "celebr-wrap",
    minW: 3,
    minH: 4,
    fixedSize: { w: 3, h: 4 },
    allowedRoles: ["admin", "hr", "member"],
  },
  "reports-analytics": {
    id: "reports-analytics",
    title: "Reports & Analytics",
    description:
      "Cross-module overview: attendance, leave, profitability, headcount.",
    icon: BarChart3,
    Component: ReportsPanel,
    className: "reports-analytics",
    // Width locked to 6 columns (= 711px at full width); height resizable.
    minW: 6,
    maxW: 6,
    minH: 6,
    resizable: true,
    allowedRoles: ["admin", "hr"],
    // Dedicated Roles & Access key: a role without it never sees this widget,
    // whatever its other report permissions. The reports API accepts it too.
    permission: "view_dashboard_reports",
  },
};

/**
 * May this dashboard kind show this widget? A widget with a `permission` is
 * shown to whoever holds that key and hidden from whoever does not — the key
 * replaces the role list, the way the backend decides access.
 */
export function isWidgetAllowed(w, role) {
  if (!w) return false;
  if (Array.isArray(w.permission)) return hasAnyPermission(w.permission);
  if (w.permission) return hasPermission(w.permission);
  return w.allowedRoles.includes(role);
}

/** Widgets visible to a given role, in registry order. */
export function widgetsForRole(role) {
  return Object.values(WIDGET_REGISTRY).filter((w) => isWidgetAllowed(w, role));
}

/**
 * Default layouts by role. 12-column grid, rowHeight 80.
 * Each item: { i, x, y, w, h, minW, minH }
 */
const ADMIN_HR_DEFAULTS = [
  { i: "weather", x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  { i: "total-employees", x: 0, y: 2, w: 3, h: 1, minW: 3, minH: 1 },
  { i: "attendance-summary", x: 0, y: 3, w: 3, h: 3, minW: 3, minH: 3 },
  { i: "star-of-the-month", x: 0, y: 6, w: 3, h: 4, minW: 2, minH: 3 },
  { i: "performance", x: 0, y: 10, w: 3, h: 3, minW: 3, minH: 2 },
  { i: "date-time-attendance", x: 3, y: 0, w: 6, h: 2, minW: 4, minH: 2 },
  { i: "daily-briefing", x: 3, y: 2, w: 6, h: 6, minW: 3, minH: 5 },
  {
    i: "reports-analytics",
    x: 3,
    y: 8,
    w: 6,
    h: 18,
    minW: 6,
    maxW: 6,
    minH: 6,
  },
  { i: "personal-notes", x: 9, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
  { i: "leave-balance", x: 9, y: 3, w: 3, h: 3, minW: 3, minH: 3 },
  { i: "employees-on-leave", x: 9, y: 6, w: 3, h: 4, minW: 3, minH: 4 },
  { i: "upcoming-holidays", x: 9, y: 10, w: 3, h: 3, minW: 3, minH: 3 },
  { i: "upcoming-celebrations", x: 9, y: 13, w: 3, h: 4, minW: 3, minH: 4 },
];

const MEMBER_DEFAULTS = [
  { i: "weather", x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  { i: "attendance-summary", x: 0, y: 2, w: 3, h: 3, minW: 3, minH: 3 },
  { i: "star-of-the-month", x: 0, y: 5, w: 3, h: 4, minW: 2, minH: 3 },
  { i: "performance", x: 0, y: 9, w: 3, h: 3, minW: 3, minH: 2 },
  { i: "date-time-attendance", x: 3, y: 0, w: 6, h: 2, minW: 4, minH: 2 },
  { i: "daily-briefing", x: 3, y: 2, w: 6, h: 6, minW: 3, minH: 5 },
  { i: "leave-balance", x: 3, y: 8, w: 6, h: 3, minW: 3, minH: 3 },
  { i: "personal-notes", x: 9, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
  { i: "employees-on-leave", x: 9, y: 3, w: 3, h: 4, minW: 3, minH: 4 },
  { i: "upcoming-holidays", x: 9, y: 7, w: 3, h: 3, minW: 3, minH: 3 },
  { i: "upcoming-celebrations", x: 9, y: 10, w: 3, h: 4, minW: 3, minH: 4 },
];

export const DEFAULT_LAYOUT_BY_ROLE = {
  admin: ADMIN_HR_DEFAULTS,
  hr: ADMIN_HR_DEFAULTS,
  member: MEMBER_DEFAULTS,
};

/**
 * Repack a 12-col layout into a narrower column count by preserving the
 * user's vertical ordering (y then x) and flowing items into rows.
 *
 * @param {Array}  items    layout items (with i, x, y, w, h, minW, minH)
 * @param {number} cols     target column count (e.g. 8, 4, 1)
 * @param {Object} opts
 * @param {string[]} opts.wideIds widgets that should go full-width at this tier
 * @param {number}   opts.defaultW fallback width for items with no override
 */
export function repackForCols(items, cols, { wideIds = [], defaultW } = {}) {
  const ordered = [...items].sort((a, b) => a.y - b.y || a.x - b.x);

  // Row-tracker: array of column occupancy per row, grown on demand.
  const rows = []; // rows[r] = used cols
  const placeAt = (r, w) => {
    while (rows.length <= r) rows.push(0);
    rows[r] += w;
  };
  const firstFitRow = (w) => {
    let r = 0;
    while (rows[r] !== undefined && rows[r] + w > cols) r += 1;
    return r;
  };

  return ordered.map((it) => {
    let w;
    if (wideIds.includes(it.i)) {
      w = cols;
    } else if (defaultW != null) {
      w = Math.min(defaultW, cols);
    } else {
      // Scale original 12-col width proportionally, min 1.
      w = Math.max(1, Math.min(cols, Math.round((it.w / 12) * cols)));
    }
    // Respect minW when possible.
    if (it.minW && it.minW <= cols) w = Math.max(w, it.minW);

    const r = firstFitRow(w);
    const x = rows[r] || 0;
    placeAt(r, w);

    return { ...it, x, y: r, w };
  });
}
